<script lang="ts" setup>
import FullScreenLoader from './ui/FullScreenLoader.vue'
import ConfirmDialog from './ui/modal/ConfirmDialog.vue'
import { onBeforeUnmount, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { createConfirmDialog } from 'vuejs-confirm-dialog'
import { useAppStore } from '@stores/app.store'
import { logInfo, logError, scopedLogger, logInfoDebug } from '@lib/utils/logUtils'
import { ConfirmDialogProps } from '@lib/models/modals'
import { useLocale } from '@composables/useLocale'
import { GTMContentGroup, LoginMethod, LoginUpdateType, LogoutType } from '@lib/models/gtm'
import { LoginType, User } from '@lib/models/identification'
import { useGtm } from '@composables/useGtm'
import { useGoToCheckout } from '@composables/useGoToCheckout'
import { useAopCoupon } from '@composables/useAopCoupon'
import { COUPON_CODE_REGEX } from '@lib/models/aop'
import { isQrCode, extractCouponCode, extractMcId, parseCouponScan } from '@lib/utils/scannerUtils'
import { purchaseService } from '@lib/services/backend/purchase/purchase.service'
import { identificationService } from '@lib/services/backend/identification/identification.service'
import { LogKind, LogOwnLevel } from '@lib/models/internal/log'
import { TRANSLATIONS_MAPPER } from '@lib/models/internal/translations'

const scannerActiveRouteNames = new Set([
  'attract',
  'anonymous-welcome',
  'prehome',
  'identification',
  'home',
  'category',
  'cart',
  'aop-coupon',
  'loyalty',
  'confirming-identity'
])

const log = scopedLogger('@component/ScannerKeepAlive.vue')

const store = useAppStore()
const { t } = useLocale()
const route = useRoute()
const router = useRouter()
const { trackLogin, trackLogout, trackLoginUpdate, trackOnCloseModal } = useGtm()
const { goToCheckout } = useGoToCheckout()
const { processCoupon } = useAopCoupon()

const scanned = ref<string>()
const processing = ref<'identification' | 'coupon'>()
let removeScannerCodeListener: (() => void) | undefined

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const { reveal, close } = createConfirmDialog(ConfirmDialog as any)

const hideLoader = () => {
  processing.value = undefined
}

const identificationErrorDialogProps = (
  type: 'loginError' | 'upgradingError'
): ConfirmDialogProps => {
  const tKey = type === 'loginError' ? 'identificationError' : 'identificationUpgradingError'
  const dialogProps = {
    title: t(TRANSLATIONS_MAPPER.MODAL.IDENTIFICATION[tKey].title),
    description: t(TRANSLATIONS_MAPPER.MODAL.IDENTIFICATION[tKey].description),
    secondaryButton: t('Volver'),
    contentGroup:
      type === 'loginError'
        ? {
            cg: GTMContentGroup.primary.USER,
            cg2: GTMContentGroup.secondary.LOGIN,
            cg3: GTMContentGroup.tertiary.ERROR
          }
        : undefined
  }
  return dialogProps
}

const couponErrorDialogProps: ConfirmDialogProps = {
  title: t(TRANSLATIONS_MAPPER.MODAL.COUPON.aopCouponError.title),
  description: t(TRANSLATIONS_MAPPER.MODAL.COUPON.aopCouponError.description),
  primaryButton: undefined,
  secondaryButton: t('Volver'),
  contentGroup: {
    cg: GTMContentGroup.primary.COUPONS,
    cg2: GTMContentGroup.secondary.ERROR
  }
}

const couponErrorWithoutSessionDialogProps: ConfirmDialogProps = {
  title: t(TRANSLATIONS_MAPPER.MODAL.COUPON.aopCouponWithoutSession.title),
  description: t(TRANSLATIONS_MAPPER.MODAL.COUPON.aopCouponWithoutSession.description),
  primaryButton: undefined,
  secondaryButton: t('Volver')
}

const identificationSuccesDialogProps = (userName: string) => {
  return {
    // eslint-disable-next-line camelcase
    title: t('hola_nombreusuario', { user_name: userName }),
    description: t('te_damos_la_bienvenida_a_mimcdonalds_ya_puedes_sumar_puntos_con_tus_compras'),
    primaryButton: t('Continuar'),
    secondaryButton: undefined,
    imageUrl: 'images/mcd.svg',
    contentGroup: {
      cg: GTMContentGroup.primary.USER,
      cg2: GTMContentGroup.secondary.LOGIN,
      cg3: GTMContentGroup.tertiary.WELCOME
    }
  }
}

const qrChangeAccountDialogProps: ConfirmDialogProps = {
  title: t('quieres_usar_otra_cuenta'),
  description: t(
    'el_qr_pertenece_a_otra_persona_si_continuas_se_vaciara_tu_carrito_y_tendras_que_comenzar_tu_pedido_otra_vez'
  ),
  primaryButton: t('mantener_cuenta'),
  secondaryButton: t('usar_otra_cuenta'),
  contentGroup: {
    cg: GTMContentGroup.primary.USER,
    cg2: GTMContentGroup.secondary.LOGIN,
    cg3: GTMContentGroup.tertiary.CHANGE
  }
}

const isActiveRoute = (): boolean => {
  if (route.name === 'checkout') {
    // check checkout only first step is available
    return route.query.step === 'payment-method'
  }
  return scannerActiveRouteNames.has(route.name as string)
}

const handleQrCode = async (code: string) => {
  const mcId = extractMcId(code)
  if (!mcId) {
    reset()
    return
  }
  // pass mcId and scanned full code (qr)
  await scanProcessIdentification(mcId, code)
}

const handleCouponCode = async (code: string) => {
  // code is the raw scan: an identified coupon carries its mcId in the 2nd field
  const { coupon, mcId } = parseCouponScan(code)
  let invalidStateCoupon = !COUPON_CODE_REGEX.test(coupon)

  if (invalidStateCoupon) {
    logInfo(log, 'Seems scanned code is not a valid coupon code')
  }

  if (!store.cart) {
    // no session available yet
    logInfo(log, 'Session is not started yet, user cannot try to apply coupon')
    const { isCanceled } = await reveal(couponErrorWithoutSessionDialogProps)
    if (isCanceled && route.name === 'attract') await router.push({ name: 'prehome' })
    reset()
    return
  }

  if (route.name === 'confirming-identity') {
    logInfo(log, 'Coupon cannot be scanned on confirming-identity')
    invalidStateCoupon = true
  }

  if (invalidStateCoupon) {
    reset()
    return
  }

  await scanProcessCoupon({ coupon, mcId, sessionId: store.cart.sessionId })
}

const onScannedCode = async (code?: string) => {
  if (!code || scanned.value) return

  // check valid route to continue scanning
  if (!isActiveRoute()) return

  scanned.value = code

  // isQrCode can't be fooled by a coupon (or a QR's mcId) that happens to
  // contain 'FF99' or '|' inside its own characters. Anything that isn't
  // recognized as either shape falls through to the coupon path (as
  // before): its own internal COUPON_CODE_REGEX check bails out safely,
  // safer than defaulting an unrecognized scan into a real login attempt.
  const isQr = isQrCode(code)
  const couponCode = extractCouponCode(code)

  logInfoDebug(log, {
    type: LogOwnLevel.DEBUG,
    kind: LogKind.DEBUG,
    data: {
      message: 'User scanned code',
      isQr,
      ...(isQr ? { scannedCode: code } : { couponCode })
    }
  })

  if (isQr) {
    await handleQrCode(code)
  } else {
    await handleCouponCode(code)
  }
}

async function eventListenerScan(ev: CustomEvent) {
  const { scanCode } = ev.detail
  await onScannedCode(scanCode)
}

const removeListener = () => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  document.removeEventListener<any>('scan', eventListenerScan, true)
}

const identificationError = async (type: 'loginError' | 'upgradingError') => {
  const isConfirmingAccountState = store.confirmingIdentity
  hideLoader()
  const { isCanceled } = await reveal(identificationErrorDialogProps(type))
  if (isCanceled) {
    // only terminate process
    reset()
    // when confirming and cancel, go to home (logout, and clear cart is done)
    if (isConfirmingAccountState) await router.push({ name: 'home' })
  }
}

const confirmingAccountError = async () => {
  const dialogProps = {
    title: t(TRANSLATIONS_MAPPER.MODAL.IDENTIFICATION.identificationUpgradingError.title),
    description: t(
      TRANSLATIONS_MAPPER.MODAL.IDENTIFICATION.identificationUpgradingError.description
    ),
    primaryButton: t('Volver'),
    secondaryButton: undefined,
    contentGroup: undefined
  }
  hideLoader()
  await reveal(dialogProps)
  // terminate process, go cart
  reset()
  await router.push({ name: 'cart' })
}

const navigateRedirectTo = async () => {
  const redirectTo = store.softUserValidationImIdentity?.redirectTo
  // validation ends here, clearing it also closes the validation dialog still open on the origin page
  store.setSoftUserValidationImIdentity()
  if (redirectTo) await router.push(redirectTo)
}

const navigateAfterWelcome = async ({ toHome }: { toHome: boolean }) => {
  if (toHome) {
    return await router.push({
      name: 'home'
    })
  }

  // if coming from attract go to prehome
  if (
    route.query.name === 'attract' ||
    route.name === 'attract' ||
    route.name === 'anonymous-welcome'
  ) {
    await router.push({
      name: 'prehome'
    })
  }
  // if on identification go to path from where you enter identification
  else if (route.name === 'identification') {
    const path = route.query.from ? (route.query.from as string) : '/home'
    await router.push({
      path
    })
  } else trackOnCloseModal() // user stay on page but now logged
}

const upgradeIdentification = async ({ qr, logKey }: { qr: string; logKey: string }) => {
  try {
    processing.value = 'identification'
    logInfo(log, `${logKey}:SAME_USER, try to upgrade to IM login`)
    store.setUser({ user: await identificationService.upgradeLogin({ qr }), qr })
    trackLoginUpdate(LoginUpdateType.UPGRADE)
    if (store.softUserValidationImIdentity) {
      // navigate to redirectTo if exist
      await navigateRedirectTo()
    }
  } catch {
    logError(log, `${logKey}:SAME_USER Could not upgrade user login`)
    await identificationError('upgradingError')
  }
}

const logoutAndCleanupOnChangeAccount = async (logKey: string) => {
  try {
    processing.value = 'identification'
    logInfo(log, `${logKey}:OTHER_USER, change account, try to logout and cleanup session`)
    await store.clearUserData({ cleanSession: false })
    trackLogout(LogoutType.LOGIN_UPDATE)
    if (store.cart)
      store.cart = await purchaseService.fetchCleanupPurchaseSession(store.cart.sessionId)
  } catch {
    logError(log, `${logKey}:OTHER_USER, Could not logout or cleanup session`)
  }
}

const alreadyLoggedProcessSoftValidationIdentity = async (
  mcId: string,
  qr: string,
  user: User
): Promise<boolean> => {
  const isSameUser = user.mcId === mcId
  // SAME User
  if (isSameUser) {
    if (user.loginType === LoginType.IM) {
      // WIP - must not enter by here, if confirming has to be a soft user ...
      logInfo(log, 'VALIDATION_IDENTITY:SAME_USER, already logged with IM')
      // dont try to login currently logged user already with IM ...
      // navigate to redirectTo if exist
      await navigateRedirectTo()
      return false
    } else {
      // @action:upgrade to IM login
      await upgradeIdentification({ qr, logKey: 'VALIDATION_IDENTITY' })
      return false
    }
  }

  // OTHER User
  const { isCanceled } = await reveal(qrChangeAccountDialogProps)
  trackOnCloseModal()
  // no close() here: the dialog is already removed once answered, and a second removal would
  // take out the last dialog in the store, the validation dialog still open on Loyalty
  if (!isCanceled) {
    logInfo(log, 'VALIDATION_IDENTITY:OTHER_USER keep account')
    await navigateAfterWelcome({ toHome: false })
    trackLoginUpdate(LoginUpdateType.SAME_USER)
    return false // @action:keep account
  }

  // @action:change account
  // logout, clear cart, login with QR (after) in regular process
  await logoutAndCleanupOnChangeAccount('VALIDATION_IDENTITY')
  // previous user is gone, validating their identity makes no sense anymore (closes the validation dialog)
  store.setSoftUserValidationImIdentity()
  return true
}

const alreadyLoggedProcessIdentification = async (
  mcId: string,
  qr: string,
  user: User
): Promise<boolean> => {
  const isSameUser = user.mcId === mcId
  // SAME User
  if (isSameUser) {
    if (user.loginType === LoginType.IM) {
      logInfo(log, 'ALREADY_LOGGED:SAME_USER, already logged with IM')
      // dont try to login currently logged user already with IM ...
      return false
    } else {
      // @action:upgrade to IM login
      await upgradeIdentification({ qr, logKey: 'ALREADY_LOGGED' })
      return false
    }
  }

  // OTHER User
  const { isCanceled } = await reveal(qrChangeAccountDialogProps)
  trackOnCloseModal()
  close() // when using global onConfirm, onCancel is needed if using await?
  if (!isCanceled) {
    logInfo(log, 'ALREADY_LOGGED:OTHER_USER keep account')
    trackLoginUpdate(LoginUpdateType.SAME_USER)
    return false // @action:keep account
  }

  // @action:change account
  // logout, clear cart, login with QR (after) in regular process
  await logoutAndCleanupOnChangeAccount('ALREADY_LOGGED')
  return true
}

const alreadyLoggedProcessConfirmingIdentity = async (
  mcId: string,
  qr: string,
  user: User
): Promise<boolean> => {
  const isSameUser = user.mcId === mcId
  // SAME User
  if (isSameUser) {
    if (user.loginType === LoginType.IM) {
      try {
        logInfo(log, 'CONFIRMING_IDENTITY:SAME_USER, try to confirming current login with IM')
        store.setUser({ user: await identificationService.confirmLogin({ qr }), qr })
        logInfo(log, 'CONFIRMING_IDENTITY:SAME_USER, confirmed indentity for user')
        store.setInactivityIsShowed(false) // reset inactivity
        trackLoginUpdate(LoginUpdateType.SAME_USER)
        // advance to checkout (re-evaluates identity gate, i.e. OTP for a QR without token)
        await goToCheckout()
      } catch {
        logError(log, 'CONFIRMING_IDENTITY:SAME_USER, Could not confirm current login with IM')
        await confirmingAccountError()
      }
    }
    return false
  }

  // OTHER User
  const { isCanceled } = await reveal(qrChangeAccountDialogProps)
  trackOnCloseModal()
  close() // when using global onConfirm, onCancel is needed if using await?
  if (!isCanceled) {
    logInfo(log, 'CONFIRMING_IDENTITY:OTHER_USER keep account')
    trackLoginUpdate(LoginUpdateType.SAME_USER)
    // stay on confirming-identity to reinforce the user must validate their own identity
    return false // @action:keep account
  }

  // @action:change account
  // logout, clear cart, login with QR (after) in regular process
  await logoutAndCleanupOnChangeAccount('CONFIRMING_IDENTITY')
  store.setInactivityIsShowed(false) // reset inactivity
  return true
}

const scanProcessIdentification = async (mcId: string, qr: string) => {
  const userLogged = store.user
  const userIsPreviouslyLogged = !!userLogged
  // captured now, the state is cleared if the user changes account
  const wasValidatingIdentity = !!store.softUserValidationImIdentity
  let mustContinue = true

  if (userLogged) {
    // check softUserValidationImIdentity
    if (store.softUserValidationImIdentity) {
      logInfo(log, 'VALIDATION_IDENTITY with qr')
      mustContinue = await alreadyLoggedProcessSoftValidationIdentity(mcId, qr, userLogged)
    } else if (store.confirmingIdentity) {
      // check confirmingIdentity (user blocked on cart)
      logInfo(log, 'CONFIRMING_IDENTITY with qr')
      mustContinue = await alreadyLoggedProcessConfirmingIdentity(mcId, qr, userLogged)
    } else {
      // check same user or other on scan QR without softUserValidationImIdentity
      mustContinue = await alreadyLoggedProcessIdentification(mcId, qr, userLogged)
    }
  }

  if (!mustContinue) {
    reset()
    return
  }

  logInfo(log, 'Starting to identificate with qr, user with mcId = ' + mcId)

  processing.value = 'identification'
  const user = await store.logInUserAction({ qr })

  if (user) {
    hideLoader()
    if (userIsPreviouslyLogged) {
      // user was previously logged, and mustContinue is true (on if userLogged branch, mustContinue returned as true means change user) so user is changing is not a fresh login
      trackLoginUpdate(LoginUpdateType.USER_CHANGE)
    }
    await reveal(identificationSuccesDialogProps(user.name))
    if (!userIsPreviouslyLogged) trackLogin(LoginMethod.QR_IDENTIFICATION)
    await navigateAfterWelcome({
      toHome: wasValidatingIdentity || store.confirmingIdentity
    })
    reset()
  } else {
    await identificationError('loginError')
  }
}

const onCancelCouponError = async () => {
  if (store.cart) await router.push({ name: 'home' })
  else await router.push({ name: 'prehome' })
}

const couponError = async (type: 'aopCouponAlreadyUsed' | 'aopCouponError' = 'aopCouponError') => {
  const dialogProps = {
    ...couponErrorDialogProps,
    title: t(TRANSLATIONS_MAPPER.MODAL.COUPON[type].title),
    description: t(TRANSLATIONS_MAPPER.MODAL.COUPON[type].description)
  }
  hideLoader()
  const { isCanceled } = await reveal(dialogProps)
  reset()
  if (isCanceled) {
    await onCancelCouponError()
  }
}

const scanProcessCoupon = async ({
  coupon,
  mcId,
  sessionId
}: {
  coupon: string
  mcId?: string
  sessionId: string
}) => {
  // the identification (if any) happens under the hood, the user only sees the coupon validation
  processing.value = 'coupon'
  const result = await processCoupon({ coupon, mcId, sessionId })
  if (result.status === 'unauthorized') return
  if (result.status === 'error') {
    await couponError(result.type)
    return
  }
  reset()
}

const reset = () => {
  scanned.value = undefined
  processing.value = undefined
}

onMounted(() => {
  removeListener()
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  document.addEventListener<any>('scan', eventListenerScan, true)
  removeScannerCodeListener = window.api.onScannerCode((code) => {
    void onScannedCode(code)
  })
})

onBeforeUnmount(() => {
  removeListener()
  removeScannerCodeListener?.()
})
</script>

<template>
  <span data-test="scanner-keep-alive" class="absolute top-0 left-0">
    <!-- Processing -->
    <transition name="fade-b" mode="out-in">
      <FullScreenLoader
        v-if="processing"
        :title="t(`${TRANSLATIONS_MAPPER.PROCESSING[processing]}`)"
        lottie-key="loader-fries"
      />
    </transition>
  </span>
</template>
